import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { RiLogoutCircleRLine } from "react-icons/ri";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { PiCopy } from "react-icons/pi";
import { IoSettingsOutline } from "react-icons/io5";
import {
  MdAccountBalanceWallet,
  MdAccountTree,
  MdApartment,
  MdBarChart,
  MdChevronRight,
  MdDescription,
  MdExpandMore,
  MdFolder,
  MdGavel,
  MdGroup,
  MdHistory,
  MdInventory2,
  MdClose,
  MdMenu,
  MdMap,
  MdShield,
  MdSpaceDashboard,
  MdStar,
} from "react-icons/md";
import "./Navbar.css";
import { LanguageContext } from "./LanguageContext";
import enTranslations from "./translations/en.json";
import guTranslations from "./translations/gu.json";
import hinTranslations from "./translations/hi.json";
import mrTranslations from "./translations/mr.json";
import { UI_TRANSLATIONS } from "./translations/ui";
import API_URL from "./Config";
import { useMenu } from "./contexts/MenuContext";
import {
  buildMenuTree,
  clearStoredMenuItems,
  normalizeMenuContextPath,
  normalizeMenuPath,
} from "./services/menuService";

const AUTH_STATE_CHANGE_EVENT = "auth-state-changed";
const NAV_TRANSLATIONS = {
  en: enTranslations,
  gu: guTranslations,
  hi: hinTranslations,
  mr: mrTranslations,
};

const iconMap = {
  account_tree: MdAccountTree,
  apartment: MdApartment,
  audit_logs: MdShield,
  bar_chart: MdBarChart,
  custody_log: MdHistory,
  dashboard: MdSpaceDashboard,
  districts: MdMap,
  file: MdDescription,
  gavel: MdGavel,
  group: MdGroup,
  history: MdHistory,
  inventory: MdInventory2,
  law_reference: MdGavel,
  logs: MdShield,
  map: MdMap,
  payments: MdAccountBalanceWallet,
  records: MdInventory2,
  reports: MdBarChart,
  role_access: MdShield,
  shield: MdShield,
  stations: MdApartment,
  star: MdStar,
  sub_divisions: MdAccountTree,
  users: MdGroup,
  menu_access: MdStar,
  "menu-access": MdStar,
};

const isPathActive = (currentPath, menuPath) => {
  const normalizedCurrentPath = normalizeMenuContextPath(currentPath);
  const normalizedMenuPath = normalizeMenuContextPath(
    normalizeMenuPath(menuPath)
  );

  if (!normalizedCurrentPath || !normalizedMenuPath) {
    return false;
  }

  return (
    normalizedCurrentPath === normalizedMenuPath ||
    normalizedCurrentPath.startsWith(`${normalizedMenuPath}/`)
  );
};

const hasActiveDescendant = (item, currentPath) => {
  if (isPathActive(currentPath, item?.path)) {
    return true;
  }

  return Array.isArray(item?.children)
    ? item.children.some((childItem) => hasActiveDescendant(childItem, currentPath))
    : false;
};

const getExpandedIdsFromActivePath = (items, currentPath) => {
  const expandedIds = new Set();

  const walk = (item) => {
    const childItems = Array.isArray(item?.children) ? item.children : [];
    const childIsActive = childItems.some((childItem) => walk(childItem));
    const selfIsActive = isPathActive(currentPath, item?.path);

    if ((selfIsActive || childIsActive) && childItems.length > 0) {
      expandedIds.add(String(item.id));
    }

    return selfIsActive || childIsActive;
  };

  (Array.isArray(items) ? items : []).forEach((item) => walk(item));

  return expandedIds;
};

const getActivePageLabel = (items, currentPath) => {
  for (const item of Array.isArray(items) ? items : []) {
    const childLabel = getActivePageLabel(item?.children, currentPath);

    if (childLabel) {
      return childLabel;
    }

    if (isPathActive(currentPath, item?.path)) {
      return item?.label || item?.key || "";
    }
  }

  return "";
};

const getIconComponent = (item) => {
  const normalizedIconName = String(item?.icon ?? item?.key ?? "")
    .trim()
    .toLowerCase();

  return iconMap[normalizedIconName] || MdFolder;
};

const getSurfaceClassName = (isActive, level) =>
  [
    "sidebar-link__surface",
    isActive ? "sidebar-link__surface--active" : "",
    level > 0 ? "sidebar-link__surface--child" : "",
  ]
    .filter(Boolean)
    .join(" ");

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    clearMenu,
    getDefaultMenuPath,
    menuError,
    menuItems,
    menuLoading,
    refreshMenuItems,
  } = useMenu();
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [lastLogin, setLastLogin] = useState(null);
  const [accountID, setAccountID] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const [userState, setUserState] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const { selectedLanguage, changeLanguage } = useContext(LanguageContext);
  const activeTranslations = {
    ...enTranslations,
    ...(NAV_TRANSLATIONS[selectedLanguage] || {}),
    ...(UI_TRANSLATIONS[selectedLanguage] || UI_TRANSLATIONS.en),
  };
  const translatedText = (key) => activeTranslations[key] ?? key;

  const activePath = useMemo(
    () => normalizeMenuContextPath(location.pathname),
    [location.pathname]
  );
  const safeMenuItems = useMemo(
    () => (Array.isArray(menuItems) ? menuItems : []),
    [menuItems]
  );
  const menuTree = useMemo(
    () => buildMenuTree(safeMenuItems),
    [safeMenuItems]
  );
  const activeMenuLabel = getActivePageLabel(menuTree, activePath);
  const pageTitle =
    activeMenuLabel ||
    (activePath === "/datalist"
      ? translatedText("case_property_register")
      : activePath === "/dashboard" || activePath === "/reports"
      ? translatedText("dashboard_title")
      : "");
  const pageSubtitle =
    activePath === "/datalist"
      ? translatedText("case_property_register_subtitle")
      : activePath === "/dashboard" || activePath === "/reports"
      ? translatedText("dashboard_subtitle")
      : "";
  const homePath = getDefaultMenuPath();

  useEffect(() => {
    const name = localStorage.getItem("userName");
    if (name) {
      setUserName(name);
    }

    const state = localStorage.getItem("userState");
    if (state) {
      setUserState(state);
    }

    const storedLastLogin = localStorage.getItem("lastLogin");
    if (storedLastLogin) {
      const date = new Date(storedLastLogin);
      const options = {
        day: "numeric",
        hour: "numeric",
        hour12: true,
        minute: "numeric",
        month: "short",
        weekday: "short",
        year: "numeric",
      };
      const formattedLastLogin = date.toLocaleString("en-US", {
        ...options,
        timeZone: "UTC",
      });
      setLastLogin(formattedLastLogin);
    }

    const storedAccountID = localStorage.getItem("accountID");
    if (storedAccountID) {
      setAccountID(storedAccountID);
    }
  }, []);

  useEffect(() => {
    document.body.classList.add("app-shell-layout");

    return () => {
      document.body.classList.remove("app-shell-layout");
    };
  }, []);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!showMessage) {
      return undefined;
    }

    const handleSettingsKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowMessage(false);
      }
    };

    document.addEventListener("keydown", handleSettingsKeyDown);

    return () => {
      document.removeEventListener("keydown", handleSettingsKeyDown);
    };
  }, [showMessage]);

  useEffect(() => {
    const activeExpandedIds = getExpandedIdsFromActivePath(menuTree, activePath);

    setExpandedItems((currentExpandedItems) => {
      const nextExpandedItems = { ...currentExpandedItems };

      activeExpandedIds.forEach((itemId) => {
        nextExpandedItems[itemId] = true;
      });

      return nextExpandedItems;
    });
  }, [activePath, menuTree]);

  const handleLanguageChange = (event) => {
    const selected = event.target.value;

    axios
      .post(
        `${API_URL}/api/change-language`,
        { preferred_language: selected },
        {
          headers: {
            Authorization: localStorage.getItem("authKey"),
          },
        }
      )
      .then(() => {
        showSnackbar("Language changed successfully");
        changeLanguage(selected);
      })
      .catch((error) => {
        console.error(
          "Error changing language:",
          error?.response?.data?.message || error.message
        );
      });
  };

  const handleSnackbarClose = (_event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarOpen(false);
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const clearSessionAndRedirect = () => {
    localStorage.removeItem("authKey");
    localStorage.removeItem("userName");
    localStorage.removeItem("preferred_language");
    localStorage.removeItem("selectedLanguage");
    localStorage.removeItem("userState");
    localStorage.removeItem("admin");
    localStorage.removeItem("role");
    localStorage.removeItem("district_id");
    localStorage.removeItem("sub_division_id");
    localStorage.removeItem("station_id");
    localStorage.removeItem("selectedFilter");
    localStorage.removeItem("selectedOption");
    localStorage.removeItem("filter_option");
    clearStoredMenuItems();
    clearMenu();
    setShowMessage(false);
    window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
    navigate("/login");
  };

  const handleLogout = async () => {
    const authKey = localStorage.getItem("authKey");

    if (!authKey) {
      clearSessionAndRedirect();
      return;
    }

    try {
      await axios.post(`${API_URL}/api/logout`, null, {
        headers: {
          Authorization: authKey,
        },
      });
    } catch (error) {
      console.error(
        "Error during logout:",
        error?.response?.data?.message || error.message
      );
    } finally {
      clearSessionAndRedirect();
    }
  };

  const handleChnagePassword = () => {
    setShowMessage(false);
    navigate("/change-password");
  };

  const handleCopyAccountID = (event) => {
    event.stopPropagation();
    navigator.clipboard
      .writeText(accountID)
      .then(() => {
        setCopyMessage("Copied");
        setTimeout(() => {
          setCopyMessage("");
        }, 3000);
      })
      .catch((error) => {
        console.error("Failed to copy: ", error);
      });
  };

  const handleToggleMenuItem = (itemId) => {
    setExpandedItems((currentExpandedItems) => ({
      ...currentExpandedItems,
      [itemId]: !currentExpandedItems[itemId],
    }));
  };

  const jurisdictionLogo =
    userState === "Gujarat"
      ? "/logo100.png"
      : userState === "Andaman and Nicobar"
      ? "/polic.png"
      : userState === "Goa"
      ? "/goa-p.png"
      : userState === "Maharashtra"
      ? "/mahaPolicelLogo2.png"
      : "/images.jpg";
  const jurisdictionName =
    userState === "Gujarat"
      ? "Gujarat Police"
      : userState === "Andaman and Nicobar"
      ? "A&N Police"
      : userState === "Goa"
      ? "Goa Police"
      : userState === "Maharashtra"
      ? "Maharashtra Police"
      : "Police Department";
  const renderMenuItems = (items, level = 0) =>
    (Array.isArray(items) ? items : []).map((item) => {
      const childItems = Array.isArray(item?.children) ? item.children : [];
      const hasChildren = childItems.length > 0;
      const itemId = String(item?.id ?? item?.key ?? item?.path ?? item?.label);
      const itemIsActive = hasActiveDescendant(item, activePath);
      const isExpanded = expandedItems[itemId] || itemIsActive;
      const IconComponent = getIconComponent(item);
      const itemPath = String(item?.path ?? "").trim();
      const label = item?.label || item?.key || "Untitled";
      const surfaceClassName = getSurfaceClassName(itemIsActive, level);

      return (
        <div
          className={`sidebar-nav-item ${
            level > 0 ? "sidebar-nav-item--child" : ""
          }`.trim()}
          key={itemId}
        >
          <div className="sidebar-nav-row">
            {itemPath ? (
              <NavLink
                className="home-link"
                onClick={() => {
                  setIsSidebarOpen(false);
                  if (hasChildren) {
                    setExpandedItems((currentExpandedItems) => ({
                      ...currentExpandedItems,
                      [itemId]: true,
                    }));
                  }
                }}
                title={label}
                to={itemPath}
              >
                <div className={surfaceClassName}>
                  <span className="sidebar-link__icon" aria-hidden="true">
                    <IconComponent />
                  </span>
                  <span className="sidebar-link__text">{label}</span>
                </div>
              </NavLink>
            ) : (
              <button
                className="home-link home-link--button"
                onClick={() => handleToggleMenuItem(itemId)}
                title={label}
                type="button"
              >
                <div className={surfaceClassName}>
                  <span className="sidebar-link__icon" aria-hidden="true">
                    <IconComponent />
                  </span>
                  <span className="sidebar-link__text">{label}</span>
                </div>
              </button>
            )}

            {hasChildren ? (
              <button
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${label}`}
                className={`sidebar-link__caret ${
                  itemIsActive ? "sidebar-link__caret--active" : ""
                }`.trim()}
                onClick={() => handleToggleMenuItem(itemId)}
                type="button"
              >
                {isExpanded ? <MdExpandMore /> : <MdChevronRight />}
              </button>
            ) : null}
          </div>

          {hasChildren && isExpanded ? (
            <div className="sidebar-submenu">{renderMenuItems(childItems, level + 1)}</div>
          ) : null}
        </div>
      );
    });

  return (
    <>
      <div className="header-main admin-header">
        <div className="header-main-container">
          <div className="header-main-left-side">
            <button
              aria-label="Open navigation"
              className="sidebar-menu-button"
              onClick={() => setIsSidebarOpen(true)}
              type="button"
            >
              <MdMenu />
            </button>
            <Link to={homePath}>
              <img
                alt="Logo"
                className="navbar-logo"
                src={jurisdictionLogo}
              />
            </Link>
            <div>
              <img alt="Logo" className="navbar-logos" src="/Main.png" />
            </div>
          </div>

          {pageTitle ? (
            <div className="header-page-heading" aria-current="page">
              <div className="header-page-title">{pageTitle}</div>
              {pageSubtitle ? (
                <div className="header-page-subtitle">{pageSubtitle}</div>
              ) : null}
            </div>
          ) : null}

          <div className="header-main-right-side">
            <div className="welcome-quote">
              <span>
                {translatedText("welcome")} {userName}
              </span>
              <div className="last-login small-font">
                <span>Last Login: {lastLogin}</span>
              </div>
            </div>
            <button
              aria-controls="account-settings-popover"
              aria-expanded={showMessage}
              aria-haspopup="dialog"
              aria-label="Open account settings"
              className={`setting-button ${
                showMessage ? "setting-button--active" : ""
              }`.trim()}
              onClick={() => setShowMessage((currentValue) => !currentValue)}
              type="button"
            >
              <IoSettingsOutline
                className="icons-setting"
              />
            </button>
          </div>
        </div>
      </div>

      {showMessage
        ? createPortal(
            <div
              className="logout-popup"
              onClick={() => setShowMessage(false)}
            >
              <section
                aria-label="Account settings"
                className="logout-popup-content"
                id="account-settings-popover"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
              >
                <div className="settings-popover__header">
                  <div>
                    <h2>Settings</h2>
                    <p>Account and language preferences</p>
                  </div>
                  <button
                    aria-label="Close account settings"
                    className="settings-popover__close"
                    onClick={() => setShowMessage(false)}
                    type="button"
                  >
                    <MdClose />
                  </button>
                </div>

                <div className="settings-popover__body">
                  <section className="settings-popover__section">
                    <h3>Account</h3>
                    <div className="account-id">
                      <div className="account-id__details">
                        <span>Account ID</span>
                        <strong>{accountID || "Not available"}</strong>
                      </div>
                      <button
                        aria-label="Copy account ID"
                        className="account-id__copy"
                        disabled={!accountID}
                        onClick={handleCopyAccountID}
                        type="button"
                      >
                        <PiCopy className="copy-icon" />
                        <span>{copyMessage || "Copy"}</span>
                      </button>
                    </div>
                    <button
                      className="change-btn"
                      onClick={handleChnagePassword}
                      type="button"
                    >
                      <span>Change password</span>
                      <MdChevronRight />
                    </button>
                  </section>

                  <fieldset className="settings-popover__section settings-popover__language">
                    <legend>Select language</legend>
                    <div className="settings-popover__language-grid">
                      {[
                        ["en", "English"],
                        ["gu", "ગુજરાતી"],
                        ["hi", "हिन्दी"],
                        ["mr", "मराठी"],
                      ].map(([value, label]) => (
                        <label
                          className="settings-popover__language-option"
                          key={value}
                        >
                          <input
                            checked={selectedLanguage === value}
                            name="language"
                            onChange={handleLanguageChange}
                            type="radio"
                            value={value}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </div>

                <div className="settings-popover__footer">
                  <button
                    className="log-out-btn"
                    onClick={handleLogout}
                    type="button"
                  >
                    <RiLogoutCircleRLine className="iconout" />
                    <span>Log out</span>
                  </button>
                </div>
              </section>
            </div>,
            document.body
          )
        : null}

      <button
        aria-label="Close navigation"
        className={`sidebar-backdrop ${
          isSidebarOpen ? "sidebar-backdrop--visible" : ""
        }`}
        onClick={() => setIsSidebarOpen(false)}
        type="button"
      />

      <aside
        aria-label="Primary navigation"
        className={`app-sidebar ${isSidebarOpen ? "app-sidebar--open" : ""}`}
      >
        <div className="sidebar-brand">
          <Link className="sidebar-brand__link" to={homePath}>
            <span className="sidebar-brand__mark">
              <img alt="" src={jurisdictionLogo} />
            </span>
            <span className="sidebar-brand__copy">
              <span className="sidebar-brand__agency">
                {jurisdictionName}
              </span>
              <span className="sidebar-brand__product">e-Muddamaal</span>
            </span>
          </Link>
          <button
            aria-label="Close navigation"
            className="sidebar-close-button"
            onClick={() => setIsSidebarOpen(false)}
            type="button"
          >
            <MdClose />
          </button>
        </div>

        <div className="sidebar-scroll">
          <div className="sidebar-section-label">Operations</div>
          <nav className="page-header">
          {menuLoading && menuTree.length === 0 ? (
            <div className="sidebar-state">
              <div className="sidebar-state__loader" />
              <div className="sidebar-state__title">Loading menu...</div>
            </div>
          ) : menuError && menuTree.length === 0 ? (
            <div className="sidebar-state sidebar-state--error">
              <div className="sidebar-state__title">Unable to load menu</div>
              <div className="sidebar-state__text">{menuError}</div>
              <button
                className="sidebar-state__button"
                onClick={refreshMenuItems}
                type="button"
              >
                Retry
              </button>
            </div>
          ) : menuTree.length === 0 ? (
            <div className="sidebar-state">
              <div className="sidebar-state__title">No modules assigned</div>
              <div className="sidebar-state__text">
                Please contact your administrator.
              </div>
            </div>
          ) : (
            <>
              {menuError ? (
                <div className="sidebar-inline-alert">
                  <div className="sidebar-inline-alert__text">{menuError}</div>
                  <button
                    className="sidebar-inline-alert__button"
                    onClick={refreshMenuItems}
                    type="button"
                  >
                    Retry
                  </button>
                </div>
              ) : null}
              {renderMenuItems(menuTree)}
            </>
          )}
          </nav>
        </div>

        <div className="sidebar-credit">
          <span className="sidebar-credit__mark" aria-hidden="true">MA</span>
          <span className="sidebar-credit__label">Developed by</span>
          <a
            className="sidebar-credit__link"
            href="https://www.migarch.in/"
            rel="noopener noreferrer"
            target="_blank"
          >
            Mig Arch
          </a>
        </div>
      </aside>

      <Snackbar
        anchorOrigin={{ horizontal: "right", vertical: "top" }}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        open={snackbarOpen}
        style={{ marginLeft: "20px", marginTop: "80px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity="success">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}
